import type { UnitType } from 'dayjs';
import { useState } from 'react';
import { useInterval } from 'react-use';

import dayjs from '@didaskill/services/dayjs';

type UseCountdownParams = {
  date: string;
  unit?: UnitType;
  interval?: number;
};

/**
 * A custom hook that calculates a countdown value based on a target date.
 * Updates the countdown at a specified interval and returns the remaining time
 * in the specified unit.
 *
 * @param accessStartDate - The target date and time for the countdown, in ISO format.
 * @param unit - The unit of time to calculate the countdown.
 * @param interval - The interval (in milliseconds) at which the countdown updates.
 *
 * @returns The remaining time until the target date, expressed in the specified unit.
 *
 * @example
 * // Example usage:
 * const countdownInSeconds = useCountDown({
 *   accessStartDate: '2024-12-01T12:00:00Z',
 *   unit: 'seconds',
 *   interval: 1000,
 * });
 *
 */
export const useCountdown = ({
  date,
  unit = 'seconds',
  interval = 1000,
}: UseCountdownParams): number => {
  const [countdown, setCountdown] = useState(dayjs(date).diff(dayjs(), unit));

  useInterval(() => {
    setCountdown(dayjs(date).diff(dayjs(), unit));
  }, interval);

  return countdown;
};
